import React, { useState, useEffect } from "react";
import "../styles/ManageImagesPage.scss";
import { config } from "../config";
import { useFooterData, useImagesByTreatmentName } from "../Apis/useApiQueries";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const BASE_URL = config.BackendUrl;

const ManageImages = () => {
  const { treatments, treatmentsRefs } = useFooterData();
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const { data: images = [], error: imagesError } =
    useImagesByTreatmentName(selectedTreatment);
  const queryClient = useQueryClient();

  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");

  // 🔹 Treatment name mapping (Hebrew to English folder names)
  const treatmentNameMap = {
    "טיפולי שיער": "HairTreatment",
    "טיפולי בוטוקס": "BotoxTreatment",
    "פיסול שפתיים": "LipsSculpting",
    "פיסול אף": "NoseSculpting",
    "איכות העור": "SkinQuality",
    "פיסול פנים": "FaceSculpting",
    "יישור שיניים": "TeethAlignment",
    "הלבנת שיניים": "TeethWhitening",
  };

  // 🔹 Extract Cloudinary public ID correctly
  const extractPublicId = (imageUrl) => {
    // Check if it's a Cloudinary URL
    if (imageUrl.includes("cloudinary.com")) {
      // For Cloudinary URLs: https://res.cloudinary.com/cloud-name/image/upload/v1234567890/treatments/HairTreatment/filename.jpg
      const urlParts = imageUrl.split("/");
      const uploadIndex = urlParts.indexOf("upload");
      if (uploadIndex !== -1 && uploadIndex + 2 < urlParts.length) {
        // Extract everything after "upload/v1234567890/" or "upload/"
        const versionIndex = uploadIndex + 1;
        let startIndex = versionIndex;

        // Skip version number if present (starts with 'v' followed by digits)
        if (urlParts[versionIndex] && /^v\d+$/.test(urlParts[versionIndex])) {
          startIndex = versionIndex + 1;
        }

        // Join the remaining parts and remove file extension
        const publicIdParts = urlParts.slice(startIndex);
        const lastPart = publicIdParts[publicIdParts.length - 1];
        publicIdParts[publicIdParts.length - 1] = lastPart.replace(
          /\.(jpg|jpeg|png|gif)$/i,
          ""
        );

        const publicId = publicIdParts.join("/");
        console.log("🔹 Cloudinary URL:", imageUrl);
        console.log("🔹 Extracted Public ID:", publicId);
        return publicId;
      }
    }

    // For local fallback URLs: /before-and-after/טיפולי שיער/filename.jpg
    const parts = imageUrl.split("/");
    let folderName = parts[parts.length - 2]; // Get folder name
    const fileName = parts[parts.length - 1].replace(
      /\.(jpg|jpeg|png|gif)$/i,
      ""
    ); // Get filename without extension

    // Map Hebrew folder name to English
    const englishFolderName = treatmentNameMap[folderName] || folderName;

    const publicId = `treatments/${englishFolderName}/${fileName}`;

    console.log("🔹 Local fallback URL:", imageUrl);
    console.log("🔹 Extracted folder:", folderName);
    console.log("🔹 Mapped to English folder:", englishFolderName);
    console.log("🔹 Final Public ID:", publicId);
    return publicId;
  };

  // 🔹 Handle treatment selection
  const handleTreatmentSelect = (e) => {
    setSelectedTreatment(e.target.value);
  };

  // 🔹 Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: async (publicId) => {
      console.log("🔹 Sending delete request for Public ID:", publicId);
      console.log(
        "🔹 Encoded URL:",
        `${BASE_URL}/upload/images/${encodeURIComponent(publicId)}`
      );
      const response = await fetch(
        `${BASE_URL}/upload/images/${encodeURIComponent(publicId)}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error deleting image");
      }
    },
    onSuccess: () => {
      setMessage("התמונה נמחקה בהצלחה!");
      setTimeout(() => setMessage(""), 3000);
      queryClient.invalidateQueries(["images", selectedTreatment]); // Refetch images
    },
    onError: (error) => {
      if (error.message.includes("not found")) {
        setMessage(
          "שגיאה: התמונה לא נמצאה בשרת הענן. ייתכן שמדובר בתמונה מקומית שלא ניתן למחוק."
        );
      } else {
        setMessage(`שגיאה במחיקת התמונה: ${error.message}`);
      }
      setTimeout(() => setMessage(""), 5000);
    },
  });

  // 🔹 Open modal to view image in full size
  const openModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  // 🔹 Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  // 🔹 Handle password authentication
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === config.correctPassword) {
      setIsAuthenticated(true);
      localStorage.setItem("authPassword", config.correctPassword);
    } else {
      setMessage("Incorrect password");
    }
  };

  useEffect(() => {
    const storedPassword = localStorage.getItem("authPassword");
    if (storedPassword === config.correctPassword) {
      setIsAuthenticated(true);
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="password-container">
        <h2>אנא הכנס את הסיסמה על מנת להכנס</h2>
        <form onSubmit={handlePasswordSubmit} className="password-form">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter Password"
            required
          />
          <button type="submit">שלח</button>
        </form>
        {message && <p className="error-message">{message}</p>}
      </div>
    );
  }

  // 🔹 Combine treatments and treatmentsRefs
  const treatmentsList = [...(treatmentsRefs.data || [])];
  (treatments.data || []).forEach((treatment) => {
    if (!treatment.treatmentRef) {
      treatmentsList.push(treatment);
    }
  });

  return (
    <div className="container">
      <h1>ניהול תמונות</h1>
      {message && <p className="message">{message}</p>}

      {/* Treatment Selection */}
      <div className="treatment-select">
        <label htmlFor="treatment">בחר טיפול מהרשימה</label>
        <select
          id="treatment"
          value={selectedTreatment}
          onChange={handleTreatmentSelect}
        >
          <option value="">בחר טיפול</option>
          {treatmentsList.map((treatment, index) => (
            <option key={`${treatment.name}-${index}`} value={treatment.name}>
              {treatment.treatmentRef ? treatment.treatmentRef : treatment.name}
            </option>
          ))}
        </select>
      </div>

      {/* Images Grid */}
      <div className="image-grid">
        {imagesError ? (
          <p>Error loading images: {imagesError.message}</p>
        ) : images.length === 0 ? (
          <p>No images available.</p>
        ) : (
          images.map((imageUrl) => {
            const publicId = extractPublicId(imageUrl);
            return (
              <div key={publicId} className="image-item">
                <img
                  src={imageUrl}
                  alt="Treatment"
                  onClick={() => openModal(imageUrl)}
                />
                <button
                  onClick={() => deleteImageMutation.mutate(publicId)}
                  className="delete-button"
                >
                  מחק תמונה
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Image Modal with Close Button */}
      {isModalOpen && selectedImage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* 🔹 Close Button (❌) */}
            <button className="close-button" onClick={closeModal}>
              X
            </button>
            <img src={selectedImage} alt="Full Size" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageImages;
